// Language translations for Lumiere landing page
export const translations = {
  en: {
    // Navbar
    nav: {
      features: "Features",
      providers: "Providers",
      openclaw: "OpenClaw",
      themes: "Themes",
      privacy: "Privacy",
      github: "GitHub",
      download: "Download",
      chromeExtension: "Chrome Extension",
    },
    // Hero Section
    hero: {
      badge: "Available on App Store & Chrome Web Store",
      title: "Your AI Agents,",
      titleHighlight: "One Beautiful App",
      description: "Powered by OpenClaw gateway with skills, missions, and cron scheduling. Connect to Claude, Ollama, Gemini, and 12+ providers through a native experience.",
      ctaPrimary: "Download on App Store",
      ctaChrome: "Add to Chrome",
      ctaSecondary: "View Source Code",
      trustBadge1: "Free & Open Source",
      trustBadge2: "MIT Licensed",
      chatHeader: "Claude 3.5",
      chatUser: "Summarize today's key points",
      chatAiIntro: "Here are today's highlights:",
      chatAiPoint1: "Product launch Q2",
      chatAiPoint2: "Design system approved",
      chatAiPoint3: "Budget increased 15%",
      chatCommand: "/favorite",
      chatCommandResponse: "saved",
      chatPlaceholder: "Type a message...",
    },
    // Providers Section
    providers: {
      title: "OpenClaw gateway and 12+ AI providers",
      subtitle: "OpenClaw at the core — plus every major AI provider",
      claude: { name: "Claude", desc: "Anthropic" },
      ollama: { name: "Ollama", desc: "Local LLMs" },
      apple: { name: "Apple Intelligence", desc: "On-device AI" },
      openclaw: { name: "OpenClaw", desc: "Open Gateway" },
      openrouter: { name: "OpenRouter", desc: "Multi-model" },
      openai: { name: "OpenAI", desc: "GPT Models" },
      gemini: { name: "Gemini", desc: "Google AI" },
      kimi: { name: "Kimi", desc: "Moonshot AI" },
    },
    // Features Section
    features: {
      label: "Features",
      title: "Everything you need to",
      titleHighlight: "talk to AI",
      description: "A native experience packed with powerful features for interacting with your AI agents through OpenClaw and other providers.",
      items: [
        {
          title: "Real-time Streaming",
          description: "WebSocket-powered conversations. See responses as they're generated with smooth, token-by-token streaming."
        },
        {
          title: "12+ Provider Support",
          description: "Connect to Claude, Ollama, OpenClaw, OpenAI, Gemini, OpenRouter, Kimi, and more. Switch between them seamlessly."
        },
        {
          title: "Voice Input",
          description: "Native iOS speech transcription. Tap to talk and let your voice do the typing with real-time transcription."
        },
        {
          title: "38+ Slash Commands",
          description: "Powerful command system with autocomplete. Control your AI agents with intuitive typed commands."
        },
        {
          title: "Biometric Security",
          description: "Lock your conversations behind Face ID or Touch ID. Your data stays private and on-device."
        },
        {
          title: "Task Scheduling",
          description: "Schedule recurring agent tasks with OpenClaw cron-style automation. Set it and forget it."
        },
        {
          title: "Deep Linking",
          description: "Open any screen directly via lumiere:// URLs. Create triggers and home screen quick actions."
        },
        {
          title: "Favorite Messages",
          description: "Save important AI responses for quick access. Build your personal knowledge base."
        },
        {
          title: "Apple Intelligence",
          description: "Native on-device AI through Apple Foundation Models. Private, fast, and deeply integrated."
        }
      ]
    },
    // How It Works Section
    howItWorks: {
      label: "How It Works",
      title: "Get started in",
      titleHighlight: "minutes",
      steps: [
        {
          title: "Download the App",
          description: "Get Lumiere from the App Store. Free to download, no sign-up required."
        },
        {
          title: "Connect Your Providers",
          description: "Add your API keys or connect to self-hosted servers like Ollama and OpenClaw."
        },
        {
          title: "Start Chatting",
          description: "Choose your AI provider and start conversing. Switch anytime."
        },
        {
          title: "Customize & Scale",
          description: "Personalize themes, set up commands, and schedule automated tasks."
        }
      ]
    },
    // OpenClaw Integration Section
    openclaw: {
      label: "OpenClaw Integration",
      title: "Supercharge your agents with",
      titleHighlight: "OpenClaw",
      description: "OpenClaw is the open-source gateway that unlocks advanced agent capabilities. Connect to the Molt Gateway for skills, missions, scheduling, and full server-side management.",
      features: [
        {
          title: "Skills System",
          description: "Extend your agents with custom skill packs. Add new capabilities, tools, and behaviors that your AI can use during conversations."
        },
        {
          title: "Missions",
          description: "Multi-step AI-driven task planning. Break complex goals into subtasks and let your agent execute them autonomously."
        },
        {
          title: "Cron Scheduler",
          description: "Schedule recurring tasks with cron-style automation. Run agent jobs on a timer — reports, summaries, monitoring, and more."
        },
        {
          title: "Molt Gateway",
          description: "WebSocket streaming protocol v3 with server-side session management, chat history persistence, and real-time data sync."
        },
        {
          title: "Gateway Monitoring",
          description: "Real-time dashboards for your gateway health. Track connections, monitor agent activity, and access detailed logs."
        },
        {
          title: "Self-Hosted Control",
          description: "Run your own OpenClaw server for complete data sovereignty. No third-party dependencies, no data leaving your infrastructure."
        }
      ],
      cta: "Learn more on GitHub"
    },
    // Chat Demo Section
    chatDemo: {
      title: "Built for power users who value",
      titleHighlight: "privacy",
      description: "Lumiere puts you in control. Connect to your own servers, keep data on-device, and customize every aspect of the experience.",
      benefits: [
        "Connect to self-hosted Ollama and OpenClaw servers",
        "Biometric lock keeps your conversations secure",
        "Backup and restore your server configurations",
        "8 color themes with automatic light/dark mode",
        "Push notifications and background task support"
      ],
      chatProvider: "Claude 3.5 Sonnet",
      chatStatus: "Connected",
      chatPlaceholder: "Type / for commands...",
    },
    // Themes Section
    themes: {
      label: "Personalization",
      title: "Make it",
      titleHighlight: "yours",
      description: "Choose from 8 beautiful color themes. Light and dark modes adapt to your system preferences automatically.",
      viewing: "Currently viewing:",
      themeNames: ["Default", "Pink", "Green", "Red", "Blue", "Purple", "Orange", "Glass"]
    },
    // Privacy Section
    privacy: {
      label: "Privacy First",
      title: "Your data stays",
      titleHighlight: "yours",
      description: "We believe AI should serve you without compromising your privacy. Lumiere is designed from the ground up to keep your conversations private.",
      features: [
        {
          title: "On-Device Processing",
          description: "Your conversations stay on your device. Apple Intelligence runs locally with zero data transmission."
        },
        {
          title: "No Tracking",
          description: "Zero analytics, no telemetry, no data collection. We don't know what you talk about."
        },
        {
          title: "Self-Host Ready",
          description: "Connect to your own Ollama or OpenClaw servers. Complete control over your AI infrastructure."
        },
        {
          title: "Biometric Lock",
          description: "Secure your conversations with Face ID or Touch ID. Even if your phone is shared, your chats are safe."
        }
      ],
      shieldTitle: "Privacy by Design",
      shieldDescription: "Every feature in Lumiere is built with privacy as a core requirement, not an afterthought.",
      stats: {
        dataCollected: "Data collected",
        localStorage: "Local storage",
        licensed: "Licensed"
      }
    },
    // Open Source Section
    openSource: {
      label: "Open Source",
      title: "Built in the",
      titleHighlight: "open",
      description: "Lumiere is fully open source. Inspect the code, contribute features, or fork it for your own use.",
      cards: [
        {
          title: "MIT Licensed",
          description: "Use it however you want. No strings attached, no vendor lock-in."
        },
        {
          title: "Community Driven",
          description: "Contributions welcome. Help build the best mobile AI client together."
        },
        {
          title: "Fork & Extend",
          description: "No tracking, no analytics. Your conversations stay between you and your AI."
        }
      ],
      viewOnGithub: "View on GitHub"
    },
    // CTA Section
    cta: {
      title: "Try Lumiere",
      titleHighlight: "Everywhere",
      description: "Experience the future of AI interaction on iOS and Chrome. Download now and start chatting with your favorite AI agents.",
      ctaPrimary: "Download on App Store",
      ctaChrome: "Add to Chrome",
      ctaSecondary: "View on GitHub",
      requirement: "iOS app requires iOS 18.0 or later. Chrome extension available now. Free to use."
    },
    // Privacy Policy Page
    privacyPolicy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: February 19, 2026",
      intro: "At Lumiere, privacy isn't just a feature — it's a core principle. This Privacy Policy explains how the Lumiere iOS application handles your information.",
      sections: [
        {
          title: "Information We Do Not Collect",
          content: "Lumiere does not collect, store, or transmit any personal information to our servers. We have no servers that receive your data. Specifically, we do not collect:\n\n• Personal identifiers (name, email, phone number)\n• Usage analytics or telemetry\n• Conversation content or chat history\n• Device identifiers or fingerprints\n• Location data\n• Crash reports or diagnostics sent to us"
        },
        {
          title: "Local Data Storage",
          content: "All your data — including conversations, server configurations, preferences, and favorites — is stored exclusively on your device using local storage. This data never leaves your device through Lumiere. You have full control over this data and can delete it at any time by removing the app."
        },
        {
          title: "Third-Party AI Providers",
          content: "Lumiere connects to third-party AI providers that you configure, such as Anthropic (Claude), Ollama, Apple Intelligence, and OpenClaw. When you send a message, it is transmitted directly from your device to the provider you selected.\n\nEach provider has its own privacy policy and data handling practices. We encourage you to review the privacy policies of any AI providers you connect to. Lumiere does not intermediate, log, or store any data exchanged between you and these providers."
        },
        {
          title: "Apple Intelligence",
          content: "When using Apple Intelligence features, all processing occurs on-device through Apple's Foundation Models. No conversation data is sent to external servers for on-device AI processing."
        },
        {
          title: "Data Security",
          content: "Lumiere supports biometric authentication (Face ID and Touch ID) to protect access to your conversations. Since all data is stored locally on your device, it benefits from iOS's built-in encryption and security features."
        },
        {
          title: "Children's Privacy",
          content: "Lumiere does not knowingly collect any information from anyone, including children under 13. Since we collect no data, there is no risk of inadvertent collection of children's information."
        },
        {
          title: "Open Source Transparency",
          content: "Lumiere is fully open source under the MIT License. You can inspect the complete source code to verify our privacy practices at any time. Transparency is the strongest form of trust."
        },
        {
          title: "Changes to This Policy",
          content: "We may update this Privacy Policy from time to time. Any changes will be reflected in the updated date at the top of this page and in the app's repository."
        },
        {
          title: "Contact",
          content: "If you have questions about this Privacy Policy, you can reach us through our GitHub repository at github.com/ajamaica/lumiere."
        }
      ]
    },
    // Footer
    footer: {
      tagline: "Your AI agents, one beautiful app. Free and open source.",
      product: "Product",
      resources: "Resources",
      legal: "Legal",
      documentation: "Documentation",
      mitLicense: "MIT License",
      privacyPolicy: "Privacy Policy",
      copyright: "All rights reserved.",
      madeWith: "Made with",
      forCommunity: "for the AI community"
    }
  },
  es: {
    // Navbar
    nav: {
      features: "Funciones",
      providers: "Proveedores",
      openclaw: "OpenClaw",
      themes: "Temas",
      privacy: "Privacidad",
      github: "GitHub",
      download: "Descargar",
      chromeExtension: "Extensión Chrome",
    },
    // Hero Section
    hero: {
      badge: "Disponible en App Store y Chrome Web Store",
      title: "Tus Agentes de IA,",
      titleHighlight: "Una App Hermosa",
      description: "Impulsado por el gateway OpenClaw con habilidades, misiones y programación cron. Conéctate a Claude, Ollama, Gemini y más de 12 proveedores a través de una experiencia nativa.",
      ctaPrimary: "Descargar en App Store",
      ctaChrome: "Agregar a Chrome",
      ctaSecondary: "Ver Código Fuente",
      trustBadge1: "Gratis y Código Abierto",
      trustBadge2: "Licencia MIT",
      chatHeader: "Claude 3.5",
      chatUser: "Resume los puntos clave de hoy",
      chatAiIntro: "Aquí están los puntos destacados:",
      chatAiPoint1: "Lanzamiento de producto Q2",
      chatAiPoint2: "Sistema de diseño aprobado",
      chatAiPoint3: "Presupuesto aumentado 15%",
      chatCommand: "/favorito",
      chatCommandResponse: "guardado",
      chatPlaceholder: "Escribe un mensaje...",
    },
    // Providers Section
    providers: {
      title: "Gateway OpenClaw y más de 12 proveedores de IA",
      subtitle: "OpenClaw en el centro — más todos los principales proveedores de IA",
      claude: { name: "Claude", desc: "Anthropic" },
      ollama: { name: "Ollama", desc: "LLMs Locales" },
      apple: { name: "Apple Intelligence", desc: "IA en dispositivo" },
      openclaw: { name: "OpenClaw", desc: "Gateway Abierto" },
      openrouter: { name: "OpenRouter", desc: "Multi-modelo" },
      openai: { name: "OpenAI", desc: "Modelos GPT" },
      gemini: { name: "Gemini", desc: "IA de Google" },
      kimi: { name: "Kimi", desc: "Moonshot AI" },
    },
    // Features Section
    features: {
      label: "Funciones",
      title: "Todo lo que necesitas para",
      titleHighlight: "hablar con IA",
      description: "Una experiencia nativa con potentes funciones para interactuar con tus agentes de IA a través de OpenClaw y otros proveedores.",
      items: [
        {
          title: "Streaming en Tiempo Real",
          description: "Conversaciones con WebSocket. Ve las respuestas mientras se generan con streaming fluido token por token."
        },
        {
          title: "Más de 12 Proveedores",
          description: "Conéctate a Claude, Ollama, OpenClaw, OpenAI, Gemini, OpenRouter, Kimi y más. Cambia entre ellos sin problemas."
        },
        {
          title: "Entrada de Voz",
          description: "Transcripción de voz nativa de iOS. Toca para hablar y deja que tu voz escriba con transcripción en tiempo real."
        },
        {
          title: "38+ Comandos Slash",
          description: "Potente sistema de comandos con autocompletado. Controla tus agentes de IA con comandos intuitivos."
        },
        {
          title: "Seguridad Biométrica",
          description: "Bloquea tus conversaciones con Face ID o Touch ID. Tus datos permanecen privados y en el dispositivo."
        },
        {
          title: "Programación de Tareas",
          description: "Programa tareas recurrentes con automatización estilo cron de OpenClaw. Configúralo y olvídate."
        },
        {
          title: "Enlaces Profundos",
          description: "Abre cualquier pantalla directamente vía URLs lumiere://. Crea disparadores y acciones rápidas."
        },
        {
          title: "Mensajes Favoritos",
          description: "Guarda respuestas importantes de IA para acceso rápido. Construye tu base de conocimiento personal."
        },
        {
          title: "Apple Intelligence",
          description: "IA nativa en dispositivo a través de Apple Foundation Models. Privada, rápida e integrada."
        }
      ]
    },
    // How It Works Section
    howItWorks: {
      label: "Cómo Funciona",
      title: "Comienza en",
      titleHighlight: "minutos",
      steps: [
        {
          title: "Descarga la App",
          description: "Obtén Lumiere en App Store. Gratis para descargar, sin registro requerido."
        },
        {
          title: "Conecta tus Proveedores",
          description: "Agrega tus claves API o conéctate a servidores autoalojados como Ollama y OpenClaw."
        },
        {
          title: "Empieza a Chatear",
          description: "Elige tu proveedor de IA y comienza a conversar. Cambia en cualquier momento."
        },
        {
          title: "Personaliza y Escala",
          description: "Personaliza temas, configura comandos y programa tareas automatizadas."
        }
      ]
    },
    // OpenClaw Integration Section
    openclaw: {
      label: "Integración OpenClaw",
      title: "Potencia tus agentes con",
      titleHighlight: "OpenClaw",
      description: "OpenClaw es el gateway de código abierto que desbloquea capacidades avanzadas de agentes. Conéctate al Molt Gateway para habilidades, misiones, programación y gestión completa del servidor.",
      features: [
        {
          title: "Sistema de Habilidades",
          description: "Extiende tus agentes con paquetes de habilidades personalizadas. Agrega nuevas capacidades, herramientas y comportamientos que tu IA puede usar durante las conversaciones."
        },
        {
          title: "Misiones",
          description: "Planificación de tareas multi-paso impulsada por IA. Descompón objetivos complejos en subtareas y deja que tu agente las ejecute de forma autónoma."
        },
        {
          title: "Programador Cron",
          description: "Programa tareas recurrentes con automatización estilo cron. Ejecuta trabajos de agentes con temporizador — reportes, resúmenes, monitoreo y más."
        },
        {
          title: "Molt Gateway",
          description: "Protocolo de streaming WebSocket v3 con gestión de sesiones del lado del servidor, persistencia de historial de chat y sincronización de datos en tiempo real."
        },
        {
          title: "Monitoreo de Gateway",
          description: "Dashboards en tiempo real para la salud de tu gateway. Rastrea conexiones, monitorea actividad de agentes y accede a registros detallados."
        },
        {
          title: "Control Autoalojado",
          description: "Ejecuta tu propio servidor OpenClaw para soberanía total de datos. Sin dependencias de terceros, sin datos saliendo de tu infraestructura."
        }
      ],
      cta: "Más información en GitHub"
    },
    // Chat Demo Section
    chatDemo: {
      title: "Construido para usuarios avanzados que valoran la",
      titleHighlight: "privacidad",
      description: "Lumiere te da el control. Conéctate a tus propios servidores, mantén los datos en el dispositivo y personaliza cada aspecto de la experiencia.",
      benefits: [
        "Conéctate a servidores Ollama y OpenClaw autoalojados",
        "El bloqueo biométrico mantiene tus conversaciones seguras",
        "Respalda y restaura tus configuraciones de servidor",
        "8 temas de color con modo claro/oscuro automático",
        "Notificaciones push y soporte de tareas en segundo plano"
      ],
      chatProvider: "Claude 3.5 Sonnet",
      chatStatus: "Conectado",
      chatPlaceholder: "Escribe / para comandos...",
    },
    // Themes Section
    themes: {
      label: "Personalización",
      title: "Hazlo",
      titleHighlight: "tuyo",
      description: "Elige entre 8 hermosos temas de color. Los modos claro y oscuro se adaptan automáticamente a las preferencias de tu sistema.",
      viewing: "Viendo actualmente:",
      themeNames: ["Predeterminado", "Rosa", "Verde", "Rojo", "Azul", "Púrpura", "Naranja", "Cristal"]
    },
    // Privacy Section
    privacy: {
      label: "Privacidad Primero",
      title: "Tus datos permanecen",
      titleHighlight: "tuyos",
      description: "Creemos que la IA debe servirte sin comprometer tu privacidad. Lumiere está diseñado desde cero para mantener tus conversaciones privadas.",
      features: [
        {
          title: "Procesamiento en Dispositivo",
          description: "Tus conversaciones permanecen en tu dispositivo. Apple Intelligence se ejecuta localmente sin transmisión de datos."
        },
        {
          title: "Sin Rastreo",
          description: "Cero analíticas, sin telemetría, sin recolección de datos. No sabemos de qué hablas."
        },
        {
          title: "Listo para Autoalojar",
          description: "Conéctate a tus propios servidores Ollama u OpenClaw. Control completo sobre tu infraestructura de IA."
        },
        {
          title: "Bloqueo Biométrico",
          description: "Asegura tus conversaciones con Face ID o Touch ID. Incluso si compartes tu teléfono, tus chats están seguros."
        }
      ],
      shieldTitle: "Privacidad por Diseño",
      shieldDescription: "Cada función en Lumiere está construida con la privacidad como requisito central, no como una ocurrencia tardía.",
      stats: {
        dataCollected: "Datos recolectados",
        localStorage: "Almacenamiento local",
        licensed: "Licenciado"
      }
    },
    // Open Source Section
    openSource: {
      label: "Código Abierto",
      title: "Construido en",
      titleHighlight: "abierto",
      description: "Lumiere es completamente de código abierto. Inspecciona el código, contribuye funciones o bifúrcalo para tu propio uso.",
      cards: [
        {
          title: "Licencia MIT",
          description: "Úsalo como quieras. Sin ataduras, sin dependencia de proveedores."
        },
        {
          title: "Impulsado por la Comunidad",
          description: "Contribuciones bienvenidas. Ayuda a construir el mejor cliente móvil de IA juntos."
        },
        {
          title: "Bifurca y Extiende",
          description: "Sin rastreo, sin analíticas. Tus conversaciones quedan entre tú y tu IA."
        }
      ],
      viewOnGithub: "Ver en GitHub"
    },
    // CTA Section
    cta: {
      title: "Prueba Lumiere",
      titleHighlight: "En Todas Partes",
      description: "Experimenta el futuro de la interacción con IA en iOS y Chrome. Descarga ahora y comienza a chatear con tus agentes de IA favoritos.",
      ctaPrimary: "Descargar en App Store",
      ctaChrome: "Agregar a Chrome",
      ctaSecondary: "Ver en GitHub",
      requirement: "La app iOS requiere iOS 18.0 o posterior. Extensión de Chrome disponible ahora. Gratis para usar."
    },
    // Privacy Policy Page
    privacyPolicy: {
      title: "Política de Privacidad",
      lastUpdated: "Última actualización: 19 de febrero de 2026",
      intro: "En Lumiere, la privacidad no es solo una función — es un principio fundamental. Esta Política de Privacidad explica cómo la aplicación Lumiere para iOS maneja tu información.",
      sections: [
        {
          title: "Información que No Recopilamos",
          content: "Lumiere no recopila, almacena ni transmite ninguna información personal a nuestros servidores. No tenemos servidores que reciban tus datos. Específicamente, no recopilamos:\n\n• Identificadores personales (nombre, correo, teléfono)\n• Analíticas de uso o telemetría\n• Contenido de conversaciones o historial de chat\n• Identificadores o huellas del dispositivo\n• Datos de ubicación\n• Informes de errores o diagnósticos enviados a nosotros"
        },
        {
          title: "Almacenamiento Local de Datos",
          content: "Todos tus datos — incluyendo conversaciones, configuraciones de servidor, preferencias y favoritos — se almacenan exclusivamente en tu dispositivo usando almacenamiento local. Estos datos nunca salen de tu dispositivo a través de Lumiere. Tienes control total sobre estos datos y puedes eliminarlos en cualquier momento desinstalando la app."
        },
        {
          title: "Proveedores de IA de Terceros",
          content: "Lumiere se conecta a proveedores de IA de terceros que tú configuras, como Anthropic (Claude), Ollama, Apple Intelligence y OpenClaw. Cuando envías un mensaje, se transmite directamente desde tu dispositivo al proveedor que seleccionaste.\n\nCada proveedor tiene su propia política de privacidad y prácticas de manejo de datos. Te recomendamos revisar las políticas de privacidad de cualquier proveedor de IA al que te conectes. Lumiere no intermedia, registra ni almacena ningún dato intercambiado entre tú y estos proveedores."
        },
        {
          title: "Apple Intelligence",
          content: "Al usar funciones de Apple Intelligence, todo el procesamiento ocurre en el dispositivo a través de los Foundation Models de Apple. No se envían datos de conversación a servidores externos para el procesamiento de IA en el dispositivo."
        },
        {
          title: "Seguridad de Datos",
          content: "Lumiere soporta autenticación biométrica (Face ID y Touch ID) para proteger el acceso a tus conversaciones. Dado que todos los datos se almacenan localmente en tu dispositivo, se benefician del cifrado y las funciones de seguridad integradas de iOS."
        },
        {
          title: "Privacidad de Menores",
          content: "Lumiere no recopila intencionalmente información de nadie, incluidos menores de 13 años. Dado que no recopilamos datos, no existe riesgo de recopilación inadvertida de información de menores."
        },
        {
          title: "Transparencia de Código Abierto",
          content: "Lumiere es completamente de código abierto bajo la Licencia MIT. Puedes inspeccionar el código fuente completo para verificar nuestras prácticas de privacidad en cualquier momento. La transparencia es la forma más fuerte de confianza."
        },
        {
          title: "Cambios a Esta Política",
          content: "Podemos actualizar esta Política de Privacidad de vez en cuando. Cualquier cambio se reflejará en la fecha de actualización en la parte superior de esta página y en el repositorio de la app."
        },
        {
          title: "Contacto",
          content: "Si tienes preguntas sobre esta Política de Privacidad, puedes contactarnos a través de nuestro repositorio de GitHub en github.com/ajamaica/lumiere."
        }
      ]
    },
    // Footer
    footer: {
      tagline: "Tus agentes de IA, una app hermosa. Gratis y código abierto.",
      product: "Producto",
      resources: "Recursos",
      legal: "Legal",
      documentation: "Documentación",
      mitLicense: "Licencia MIT",
      privacyPolicy: "Política de Privacidad",
      copyright: "Todos los derechos reservados.",
      madeWith: "Hecho con",
      forCommunity: "para la comunidad de IA"
    }
  },
  zh: {
    // Navbar
    nav: {
      features: "功能",
      providers: "提供商",
      openclaw: "OpenClaw",
      themes: "主题",
      privacy: "隐私",
      github: "GitHub",
      download: "下载",
      chromeExtension: "Chrome 扩展",
    },
    // Hero Section
    hero: {
      badge: "现已上架 App Store 和 Chrome 网上应用店",
      title: "你的 AI 智能体，",
      titleHighlight: "一款精美应用",
      description: "由 OpenClaw 网关驱动，支持技能、任务和定时调度。通过原生体验连接 Claude、Ollama、Gemini 等 12+ 提供商。",
      ctaPrimary: "在 App Store 下载",
      ctaChrome: "添加到 Chrome",
      ctaSecondary: "查看源代码",
      trustBadge1: "免费开源",
      trustBadge2: "MIT 许可证",
      chatHeader: "Claude 3.5",
      chatUser: "总结今天的要点",
      chatAiIntro: "以下是今天的重点：",
      chatAiPoint1: "产品 Q2 发布",
      chatAiPoint2: "设计系统已批准",
      chatAiPoint3: "预算增加 15%",
      chatCommand: "/收藏",
      chatCommandResponse: "已保存",
      chatPlaceholder: "输入消息...",
    },
    // Providers Section
    providers: {
      title: "OpenClaw 网关与 12+ AI 提供商",
      subtitle: "以 OpenClaw 为核心 — 加上所有主流 AI 提供商",
      claude: { name: "Claude", desc: "Anthropic" },
      ollama: { name: "Ollama", desc: "本地大模型" },
      apple: { name: "Apple Intelligence", desc: "设备端 AI" },
      openclaw: { name: "OpenClaw", desc: "开放网关" },
      openrouter: { name: "OpenRouter", desc: "多模型" },
      openai: { name: "OpenAI", desc: "GPT 模型" },
      gemini: { name: "Gemini", desc: "Google AI" },
      kimi: { name: "Kimi", desc: "月之暗面" },
    },
    // Features Section
    features: {
      label: "功能",
      title: "与 AI 对话所需的",
      titleHighlight: "一切",
      description: "原生体验，集成强大功能，通过 OpenClaw 及其他提供商与你的 AI 智能体交互。",
      items: [
        {
          title: "实时流式传输",
          description: "基于 WebSocket 的对话。流畅的逐字生成，实时查看响应。"
        },
        {
          title: "12+ 提供商支持",
          description: "连接 Claude、Ollama、OpenClaw、OpenAI、Gemini、OpenRouter、Kimi 等。无缝切换。"
        },
        {
          title: "语音输入",
          description: "原生 iOS 语音转文字。点击即可说话，实时转录。"
        },
        {
          title: "38+ 斜杠命令",
          description: "强大的命令系统，支持自动补全。通过直观的命令控制你的 AI 智能体。"
        },
        {
          title: "生物识别安全",
          description: "用 Face ID 或 Touch ID 锁定对话。数据保持私密，存储在设备上。"
        },
        {
          title: "任务调度",
          description: "使用 OpenClaw 的 cron 风格自动化调度周期性任务。设置后即可放心。"
        },
        {
          title: "深度链接",
          description: "通过 lumiere:// URL 直接打开任何页面。创建触发器和主屏幕快捷操作。"
        },
        {
          title: "收藏消息",
          description: "保存重要的 AI 回复以便快速访问。构建你的个人知识库。"
        },
        {
          title: "Apple Intelligence",
          description: "通过 Apple Foundation Models 实现原生设备端 AI。私密、快速、深度集成。"
        }
      ]
    },
    // How It Works Section
    howItWorks: {
      label: "使用方法",
      title: "几分钟内",
      titleHighlight: "即可开始",
      steps: [
        {
          title: "下载应用",
          description: "从 App Store 获取 Lumiere。免费下载，无需注册。"
        },
        {
          title: "连接提供商",
          description: "添加你的 API 密钥或连接到 Ollama、OpenClaw 等自托管服务器。"
        },
        {
          title: "开始聊天",
          description: "选择你的 AI 提供商并开始对话。随时切换。"
        },
        {
          title: "自定义与扩展",
          description: "个性化主题，设置命令，调度自动化任务。"
        }
      ]
    },
    // OpenClaw Integration Section
    openclaw: {
      label: "OpenClaw 集成",
      title: "用 OpenClaw",
      titleHighlight: "增强你的智能体",
      description: "OpenClaw 是解锁高级智能体能力的开源网关。连接 Molt Gateway 获取技能、任务、调度和完整的服务端管理。",
      features: [
        {
          title: "技能系统",
          description: "通过自定义技能包扩展你的智能体。添加 AI 在对话中可使用的新能力、工具和行为。"
        },
        {
          title: "任务系统",
          description: "AI 驱动的多步骤任务规划。将复杂目标分解为子任务，让智能体自主执行。"
        },
        {
          title: "Cron 调度器",
          description: "使用 cron 风格自动化调度周期性任务。按计划运行智能体工作 — 报告、摘要、监控等。"
        },
        {
          title: "Molt 网关",
          description: "WebSocket 流式协议 v3，支持服务端会话管理、聊天历史持久化和实时数据同步。"
        },
        {
          title: "网关监控",
          description: "实时仪表板监控网关健康状态。跟踪连接、监控智能体活动、查看详细日志。"
        },
        {
          title: "自托管控制",
          description: "运行你自己的 OpenClaw 服务器，完全掌控数据主权。无第三方依赖，数据不离开你的基础设施。"
        }
      ],
      cta: "在 GitHub 上了解更多"
    },
    // Chat Demo Section
    chatDemo: {
      title: "为重视隐私的高级用户",
      titleHighlight: "而打造",
      description: "Lumiere 让你掌控一切。连接你自己的服务器，数据保留在设备上，自定义体验的每个方面。",
      benefits: [
        "连接自托管的 Ollama 和 OpenClaw 服务器",
        "生物识别锁定保障对话安全",
        "备份和恢复你的服务器配置",
        "8 种配色主题，自动明暗模式",
        "推送通知和后台任务支持"
      ],
      chatProvider: "Claude 3.5 Sonnet",
      chatStatus: "已连接",
      chatPlaceholder: "输入 / 获取命令...",
    },
    // Themes Section
    themes: {
      label: "个性化",
      title: "让它成为",
      titleHighlight: "你的风格",
      description: "从 8 种精美配色主题中选择。明暗模式自动适配你的系统偏好。",
      viewing: "当前查看：",
      themeNames: ["默认", "粉色", "绿色", "红色", "蓝色", "紫色", "橙色", "玻璃"]
    },
    // Privacy Section
    privacy: {
      label: "隐私优先",
      title: "你的数据",
      titleHighlight: "由你掌控",
      description: "我们相信 AI 应该为你服务而不牺牲隐私。Lumiere 从底层设计就致力于保护你的对话隐私。",
      features: [
        {
          title: "设备端处理",
          description: "对话留在你的设备上。Apple Intelligence 完全在本地运行，零数据传输。"
        },
        {
          title: "无追踪",
          description: "零分析、无遥测、无数据收集。我们不知道你聊了什么。"
        },
        {
          title: "支持自托管",
          description: "连接到你自己的 Ollama 或 OpenClaw 服务器。完全掌控你的 AI 基础设施。"
        },
        {
          title: "生物识别锁",
          description: "用 Face ID 或 Touch ID 保护对话。即使手机被他人使用，聊天记录也是安全的。"
        }
      ],
      shieldTitle: "隐私设计",
      shieldDescription: "Lumiere 的每个功能都以隐私为核心要求，而非事后补救。",
      stats: {
        dataCollected: "数据收集",
        localStorage: "本地存储",
        licensed: "许可证"
      }
    },
    // Open Source Section
    openSource: {
      label: "开源",
      title: "以开放方式",
      titleHighlight: "构建",
      description: "Lumiere 完全开源。审查代码、贡献功能或 Fork 到你自己的项目。",
      cards: [
        {
          title: "MIT 许可证",
          description: "随意使用。无附加条件，无供应商锁定。"
        },
        {
          title: "社区驱动",
          description: "欢迎贡献。一起构建最好的移动端 AI 客户端。"
        },
        {
          title: "Fork 和扩展",
          description: "无追踪，无分析。你的对话只属于你和你的 AI。"
        }
      ],
      viewOnGithub: "在 GitHub 上查看"
    },
    // CTA Section
    cta: {
      title: "体验 Lumiere",
      titleHighlight: "随处可用",
      description: "在 iOS 和 Chrome 上体验 AI 交互的未来。立即下载，开始与你喜爱的 AI 智能体聊天。",
      ctaPrimary: "在 App Store 下载",
      ctaChrome: "添加到 Chrome",
      ctaSecondary: "在 GitHub 上查看",
      requirement: "iOS 应用需要 iOS 18.0 或更高版本。Chrome 扩展现已推出。免费使用。"
    },
    // Privacy Policy Page
    privacyPolicy: {
      title: "隐私政策",
      lastUpdated: "最后更新：2026年2月19日",
      intro: "在 Lumiere，隐私不仅仅是一项功能 — 而是核心原则。本隐私政策说明了 Lumiere iOS 应用如何处理你的信息。",
      sections: [
        {
          title: "我们不收集的信息",
          content: "Lumiere 不会向我们的服务器收集、存储或传输任何个人信息。我们没有接收你数据的服务器。具体来说，我们不收集：\n\n• 个人身份信息（姓名、邮箱、电话号码）\n• 使用分析或遥测数据\n• 对话内容或聊天记录\n• 设备标识或指纹\n• 位置数据\n• 发送给我们的崩溃报告或诊断信息"
        },
        {
          title: "本地数据存储",
          content: "你的所有数据 — 包括对话、服务器配置、偏好设置和收藏 — 都专门存储在你设备的本地存储中。这些数据永远不会通过 Lumiere 离开你的设备。你可以随时通过删除应用来完全控制和清除这些数据。"
        },
        {
          title: "第三方 AI 提供商",
          content: "Lumiere 连接到你配置的第三方 AI 提供商，如 Anthropic（Claude）、Ollama、Apple Intelligence 和 OpenClaw。当你发送消息时，它会直接从你的设备传输到你选择的提供商。\n\n每个提供商都有自己的隐私政策和数据处理惯例。我们建议你查看所连接的任何 AI 提供商的隐私政策。Lumiere 不会中转、记录或存储你与这些提供商之间交换的任何数据。"
        },
        {
          title: "Apple Intelligence",
          content: "使用 Apple Intelligence 功能时，所有处理均通过 Apple Foundation Models 在设备上进行。设备端 AI 处理不会将对话数据发送到外部服务器。"
        },
        {
          title: "数据安全",
          content: "Lumiere 支持生物识别认证（Face ID 和 Touch ID）来保护对话访问。由于所有数据都存储在你的设备本地，因此受益于 iOS 内置的加密和安全功能。"
        },
        {
          title: "儿童隐私",
          content: "Lumiere 不会有意收集任何人的信息，包括 13 岁以下儿童。由于我们不收集数据，因此不存在无意收集儿童信息的风险。"
        },
        {
          title: "开源透明",
          content: "Lumiere 在 MIT 许可证下完全开源。你可以随时检查完整的源代码来验证我们的隐私实践。透明是信任的最强形式。"
        },
        {
          title: "政策变更",
          content: "我们可能会不时更新本隐私政策。任何变更都将反映在本页顶部的更新日期和应用的代码仓库中。"
        },
        {
          title: "联系我们",
          content: "如果你对本隐私政策有疑问，可以通过我们的 GitHub 仓库 github.com/ajamaica/lumiere 联系我们。"
        }
      ]
    },
    // Footer
    footer: {
      tagline: "你的 AI 智能体，一款精美应用。免费开源。",
      product: "产品",
      resources: "资源",
      legal: "法律",
      documentation: "文档",
      mitLicense: "MIT 许可证",
      privacyPolicy: "隐私政策",
      copyright: "保留所有权利。",
      madeWith: "用",
      forCommunity: "为 AI 社区而作"
    }
  },
  ja: {
    // Navbar
    nav: {
      features: "機能",
      providers: "プロバイダー",
      openclaw: "OpenClaw",
      themes: "テーマ",
      privacy: "プライバシー",
      github: "GitHub",
      download: "ダウンロード",
      chromeExtension: "Chrome 拡張機能",
    },
    // Hero Section
    hero: {
      badge: "App Store & Chrome ウェブストアで公開中",
      title: "あなたのAIエージェント、",
      titleHighlight: "美しいアプリで",
      description: "OpenClawゲートウェイによるスキル、ミッション、cronスケジューリング。Claude、Ollama、Geminiなど12以上のプロバイダーにネイティブ体験で接続。",
      ctaPrimary: "App Storeでダウンロード",
      ctaChrome: "Chromeに追加",
      ctaSecondary: "ソースコードを見る",
      trustBadge1: "無料＆オープンソース",
      trustBadge2: "MITライセンス",
      chatHeader: "Claude 3.5",
      chatUser: "今日の要点をまとめて",
      chatAiIntro: "今日のハイライトです：",
      chatAiPoint1: "製品Q2ローンチ",
      chatAiPoint2: "デザインシステム承認",
      chatAiPoint3: "予算15%増加",
      chatCommand: "/お気に入り",
      chatCommandResponse: "保存済み",
      chatPlaceholder: "メッセージを入力...",
    },
    // Providers Section
    providers: {
      title: "OpenClawゲートウェイと12以上のAIプロバイダー",
      subtitle: "OpenClawを中心に — すべての主要AIプロバイダーを網羅",
      claude: { name: "Claude", desc: "Anthropic" },
      ollama: { name: "Ollama", desc: "ローカルLLM" },
      apple: { name: "Apple Intelligence", desc: "オンデバイスAI" },
      openclaw: { name: "OpenClaw", desc: "オープンゲートウェイ" },
      openrouter: { name: "OpenRouter", desc: "マルチモデル" },
      openai: { name: "OpenAI", desc: "GPTモデル" },
      gemini: { name: "Gemini", desc: "Google AI" },
      kimi: { name: "Kimi", desc: "Moonshot AI" },
    },
    // Features Section
    features: {
      label: "機能",
      title: "AIと対話するための",
      titleHighlight: "すべて",
      description: "OpenClawやその他のプロバイダーを通じてAIエージェントと対話するための強力な機能を備えたネイティブ体験。",
      items: [
        {
          title: "リアルタイムストリーミング",
          description: "WebSocket接続による会話。トークンごとのスムーズなストリーミングで、レスポンスをリアルタイムに確認。"
        },
        {
          title: "12以上のプロバイダー対応",
          description: "Claude、Ollama、OpenClaw、OpenAI、Gemini、OpenRouter、Kimiなどに接続。シームレスに切り替え。"
        },
        {
          title: "音声入力",
          description: "iOSネイティブの音声認識。タップして話すだけで、リアルタイムに文字起こし。"
        },
        {
          title: "38以上のスラッシュコマンド",
          description: "オートコンプリート付きの強力なコマンドシステム。直感的なコマンドでAIエージェントを操作。"
        },
        {
          title: "生体認証セキュリティ",
          description: "Face IDまたはTouch IDで会話をロック。データはプライベートにデバイス上に保管。"
        },
        {
          title: "タスクスケジューリング",
          description: "OpenClawのcronスタイル自動化で定期タスクをスケジュール。設定したら忘れてOK。"
        },
        {
          title: "ディープリンク",
          description: "lumiere:// URLで任意の画面を直接オープン。トリガーやホーム画面クイックアクションを作成。"
        },
        {
          title: "お気に入りメッセージ",
          description: "重要なAIの回答を保存してすぐにアクセス。個人のナレッジベースを構築。"
        },
        {
          title: "Apple Intelligence",
          description: "Apple Foundation ModelsによるネイティブオンデバイスAI。プライベートで高速、深く統合。"
        }
      ]
    },
    // How It Works Section
    howItWorks: {
      label: "使い方",
      title: "数分で",
      titleHighlight: "始められます",
      steps: [
        {
          title: "アプリをダウンロード",
          description: "App StoreからLumiereを入手。無料ダウンロード、登録不要。"
        },
        {
          title: "プロバイダーを接続",
          description: "APIキーを追加するか、OllamaやOpenClawなどのセルフホストサーバーに接続。"
        },
        {
          title: "チャットを開始",
          description: "AIプロバイダーを選んで会話を開始。いつでも切り替え可能。"
        },
        {
          title: "カスタマイズ＆拡張",
          description: "テーマの個人設定、コマンドの設定、自動化タスクのスケジュール。"
        }
      ]
    },
    // OpenClaw Integration Section
    openclaw: {
      label: "OpenClaw統合",
      title: "OpenClawで",
      titleHighlight: "エージェントを強化",
      description: "OpenClawは高度なエージェント機能を解放するオープンソースゲートウェイです。Molt Gatewayに接続して、スキル、ミッション、スケジューリング、完全なサーバーサイド管理を利用。",
      features: [
        {
          title: "スキルシステム",
          description: "カスタムスキルパックでエージェントを拡張。会話中にAIが使える新しい能力、ツール、動作を追加。"
        },
        {
          title: "ミッション",
          description: "AI駆動の多段階タスク計画。複雑な目標をサブタスクに分解し、エージェントに自律的に実行させる。"
        },
        {
          title: "Cronスケジューラー",
          description: "cronスタイルの自動化で定期タスクをスケジュール。タイマーでエージェントジョブを実行 — レポート、サマリー、モニタリングなど。"
        },
        {
          title: "Moltゲートウェイ",
          description: "WebSocketストリーミングプロトコルv3。サーバーサイドセッション管理、チャット履歴の永続化、リアルタイムデータ同期。"
        },
        {
          title: "ゲートウェイモニタリング",
          description: "ゲートウェイの健全性をリアルタイムダッシュボードで確認。接続の追跡、エージェント活動の監視、詳細ログへのアクセス。"
        },
        {
          title: "セルフホスト管理",
          description: "自分のOpenClawサーバーを運用して完全なデータ主権を確保。サードパーティ依存なし、インフラからデータ流出なし。"
        }
      ],
      cta: "GitHubで詳しく見る"
    },
    // Chat Demo Section
    chatDemo: {
      title: "プライバシーを重視するパワーユーザーの",
      titleHighlight: "ために",
      description: "Lumiereはあなたにコントロールを委ねます。自分のサーバーに接続し、データをデバイスに保持し、体験のあらゆる面をカスタマイズ。",
      benefits: [
        "セルフホストのOllamaとOpenClawサーバーに接続",
        "生体認証ロックで会話を安全に保護",
        "サーバー設定のバックアップと復元",
        "8種類のカラーテーマ、自動ライト/ダークモード",
        "プッシュ通知とバックグラウンドタスクサポート"
      ],
      chatProvider: "Claude 3.5 Sonnet",
      chatStatus: "接続済み",
      chatPlaceholder: "/ でコマンドを入力...",
    },
    // Themes Section
    themes: {
      label: "パーソナライゼーション",
      title: "あなただけの",
      titleHighlight: "スタイルに",
      description: "8種類の美しいカラーテーマから選択。ライトモードとダークモードはシステム設定に自動で適応。",
      viewing: "現在表示中：",
      themeNames: ["デフォルト", "ピンク", "グリーン", "レッド", "ブルー", "パープル", "オレンジ", "グラス"]
    },
    // Privacy Section
    privacy: {
      label: "プライバシー最優先",
      title: "あなたのデータは",
      titleHighlight: "あなたのもの",
      description: "AIはプライバシーを損なうことなく利用者に奉仕すべきだと考えます。Lumiereは会話のプライバシーを守るためにゼロから設計されています。",
      features: [
        {
          title: "オンデバイス処理",
          description: "会話はデバイス上に保持。Apple Intelligenceはデータ送信ゼロでローカル実行。"
        },
        {
          title: "トラッキングなし",
          description: "アナリティクスゼロ、テレメトリなし、データ収集なし。会話内容を私たちは知りません。"
        },
        {
          title: "セルフホスト対応",
          description: "自分のOllamaまたはOpenClawサーバーに接続。AIインフラを完全にコントロール。"
        },
        {
          title: "生体認証ロック",
          description: "Face IDまたはTouch IDで会話を保護。スマートフォンを共有しても、チャットは安全。"
        }
      ],
      shieldTitle: "プライバシー・バイ・デザイン",
      shieldDescription: "Lumiereのすべての機能は、後付けではなく、プライバシーを核心的要件として構築されています。",
      stats: {
        dataCollected: "データ収集",
        localStorage: "ローカルストレージ",
        licensed: "ライセンス"
      }
    },
    // Open Source Section
    openSource: {
      label: "オープンソース",
      title: "オープンに",
      titleHighlight: "構築",
      description: "Lumiereは完全オープンソース。コードを検査し、機能を貢献し、自分用にフォーク。",
      cards: [
        {
          title: "MITライセンス",
          description: "自由に使用。制約なし、ベンダーロックインなし。"
        },
        {
          title: "コミュニティ主導",
          description: "貢献を歓迎します。最高のモバイルAIクライアントを一緒に構築しましょう。"
        },
        {
          title: "フォーク＆拡張",
          description: "トラッキングなし、アナリティクスなし。会話はあなたとAIだけのもの。"
        }
      ],
      viewOnGithub: "GitHubで見る"
    },
    // CTA Section
    cta: {
      title: "Lumiereを",
      titleHighlight: "どこでも体験",
      description: "iOSとChromeでAIインタラクションの未来を体験。今すぐダウンロードして、お気に入りのAIエージェントとチャットを始めましょう。",
      ctaPrimary: "App Storeでダウンロード",
      ctaChrome: "Chromeに追加",
      ctaSecondary: "GitHubで見る",
      requirement: "iOSアプリにはiOS 18.0以降が必要です。Chrome拡張機能は公開中。無料でご利用いただけます。"
    },
    // Privacy Policy Page
    privacyPolicy: {
      title: "プライバシーポリシー",
      lastUpdated: "最終更新：2026年2月19日",
      intro: "Lumiereにおいて、プライバシーは単なる機能ではなく、核心的な原則です。このプライバシーポリシーでは、Lumiere iOSアプリがお客様の情報をどのように取り扱うかについて説明します。",
      sections: [
        {
          title: "収集しない情報",
          content: "Lumiereは、いかなる個人情報も当社のサーバーに収集、保存、送信しません。お客様のデータを受信するサーバーはありません。具体的には、以下を収集しません：\n\n• 個人識別情報（氏名、メールアドレス、電話番号）\n• 使用状況分析やテレメトリ\n• 会話内容やチャット履歴\n• デバイス識別子やフィンガープリント\n• 位置データ\n• 当社に送信されるクラッシュレポートや診断情報"
        },
        {
          title: "ローカルデータストレージ",
          content: "会話、サーバー設定、環境設定、お気に入りを含むすべてのデータは、デバイスのローカルストレージにのみ保存されます。これらのデータがLumiereを通じてデバイスから出ることはありません。アプリを削除することで、いつでもこれらのデータを完全に管理・削除できます。"
        },
        {
          title: "サードパーティAIプロバイダー",
          content: "Lumiereは、Anthropic（Claude）、Ollama、Apple Intelligence、OpenClawなど、お客様が設定したサードパーティAIプロバイダーに接続します。メッセージを送信すると、デバイスから選択したプロバイダーに直接送信されます。\n\n各プロバイダーには独自のプライバシーポリシーとデータ取り扱い慣行があります。接続するAIプロバイダーのプライバシーポリシーを確認することをお勧めします。Lumiereは、お客様とこれらのプロバイダー間で交換されるデータを仲介、記録、保存しません。"
        },
        {
          title: "Apple Intelligence",
          content: "Apple Intelligence機能を使用する場合、すべての処理はApple Foundation Modelsを通じてデバイス上で行われます。オンデバイスAI処理では、会話データが外部サーバーに送信されることはありません。"
        },
        {
          title: "データセキュリティ",
          content: "Lumiereは、会話へのアクセスを保護するために生体認証（Face IDおよびTouch ID）をサポートしています。すべてのデータはデバイスにローカル保存されるため、iOSに内蔵された暗号化とセキュリティ機能の恩恵を受けます。"
        },
        {
          title: "お子様のプライバシー",
          content: "Lumiereは、13歳未満のお子様を含め、いかなる方の情報も意図的に収集しません。データを収集しないため、お子様の情報を不意に収集するリスクはありません。"
        },
        {
          title: "オープンソースの透明性",
          content: "LumiereはMITライセンスの下で完全にオープンソースです。プライバシーの実践を確認するために、いつでも完全なソースコードを検査できます。透明性は信頼の最も強力な形です。"
        },
        {
          title: "ポリシーの変更",
          content: "本プライバシーポリシーは随時更新される場合があります。変更がある場合は、このページ上部の更新日とアプリのリポジトリに反映されます。"
        },
        {
          title: "お問い合わせ",
          content: "本プライバシーポリシーについてご質問がある場合は、GitHubリポジトリ github.com/ajamaica/lumiere を通じてお問い合わせください。"
        }
      ]
    },
    // Footer
    footer: {
      tagline: "あなたのAIエージェント、美しいアプリで。無料＆オープンソース。",
      product: "製品",
      resources: "リソース",
      legal: "法的情報",
      documentation: "ドキュメント",
      mitLicense: "MITライセンス",
      privacyPolicy: "プライバシーポリシー",
      copyright: "All rights reserved.",
      madeWith: "",
      forCommunity: "をAIコミュニティのために"
    }
  }
};

export const getTranslation = (lang, key) => {
  const keys = key.split('.');
  let value = translations[lang];
  for (const k of keys) {
    value = value?.[k];
  }
  return value || key;
};
